import { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

import 'swiper/css';
import 'swiper/css/navigation';

export default function EducationSection() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);

  const certificates = [
    {
      src: 'https://i.ibb.co/DHsqrQFY/63.png',
      title: 'NeuroGraphica Instructor',
      institution: 'Psychology of Creativity Institute',
      year: '2022',
      alt: 'NeuroGraphica Instructor Certificate from Psychology of Creativity Institute 2022'
    },
    {
      src: 'https://i.ibb.co/7NLqGjQw/78.png',
      title: 'NeuroGraphica Master',
      institution: 'Psychology of Creativity Institute',
      year: '2022',
      alt: 'NeuroGraphica Master Certificate from Psychology of Creativity Institute 2022'
    },
    {
      src: 'https://i.ibb.co/Z6kb4DzL/112.png',
      title: 'NG Aesthetic Coaching',
      institution: 'Psychology of Creativity Institute',
      year: '2022',
      alt: 'NG Aesthetic Coaching Certificate from Psychology of Creativity Institute 2022'
    },
    {
      src: 'https://i.ibb.co/rfRf2Hrq/Art-therapy.jpg',
      title: 'Art Therapy for Self-Healing Diploma',
      institution: 'Renaissance Life Therapies',
      year: '2022',
      alt: 'Art Therapy for Self-Healing Diploma from Renaissance Life Therapies 2022'
    },
    {
      src: 'https://i.ibb.co/j98GfbSm/CPD-Certificate.jpg',
      title: 'CPD Certified Training',
      institution: 'The CPD Standards Office',
      year: 'Provider No. 50070',
      alt: 'CPD Certified Training Certificate from The CPD Standards Office Provider No. 50070'
    },
    {
      src: 'https://static.readdy.ai/image/36990eb3aa57e3c7d396ac7e72f77043/6ddfb1af03351823009a68890ae27cf3.jpeg',
      title: 'Advanced Professional Training Certificate',
      institution: 'Institute of Practical Psychology and Psychological Counseling, Moscow, Russia',
      year: '2023–2024',
      alt: 'Advanced Professional Training Certificate Contemporary Practical Art Therapy from Institute of Practical Psychology and Psychological Counseling Moscow Russia 2023-2024'
    },
    {
      src: 'https://static.readdy.ai/image/36990eb3aa57e3c7d396ac7e72f77043/0aacd43ac59becc6363ca9f5bca0ead8.jpeg',
      title: 'Professional Retraining Diploma',
      institution: 'Academy of Continuing Professional Education and Development of Modern Competencies, Russia',
      year: '2024–2025',
      alt: 'Professional Retraining Diploma Psychologist-Consultant Art Therapist from Academy of Continuing Professional Education and Development of Modern Competencies Russia 2024-2025'
    }
  ];

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentImageIndex]);

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
    if (swiperInstance?.autoplay) {
      swiperInstance.autoplay.stop();
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
    if (swiperInstance?.autoplay) {
      swiperInstance.autoplay.start();
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % certificates.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + certificates.length) % certificates.length);
  };

  return (
    <>
      <section
        id="education"
        className="py-20 cinematic-bg-light"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="text-center mb-16">
            <h2
              className="text-4xl lg:text-5xl font-bold text-center relative inline-block cinematic-text-dark"
              style={{
                fontFamily: 'Zain, sans-serif',
                fontWeight: '700',
                lineHeight: '1.3'
              }}
            >
              Education & Certification
              {/* Accent underline */}
              <div
                className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-0.5"
                style={{ backgroundColor: '#D8892B' }}
              ></div>
            </h2>
          </div>

          {/* Carousel Container */}
          <div className="relative px-14 md:px-16">
            <Swiper
              modules={[Autoplay, Navigation]}
              loop={true}
              slidesPerView={1}
              spaceBetween={24}
              autoplay={{
                delay: 2500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
              }}
              breakpoints={{
                768: {
                  slidesPerView: 2,
                  spaceBetween: 24
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 24
                }
              }}
              navigation={{
                prevEl: '.edu-prev-btn',
                nextEl: '.edu-next-btn'
              }}
              onSwiper={setSwiperInstance}
              className="!overflow-visible"
            >
              {certificates.map((certificate, index) => (
                <SwiperSlide key={index}>
                  <div
                    className="group cursor-pointer"
                    onClick={() => openLightbox(index)}
                  >
                    {/* Certificate Container */}
                    <div
                      className="bg-white rounded-lg p-4 transition-all duration-300 hover:shadow-2xl"
                      style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      {/* Certificate Image */}
                      <div className="w-full aspect-[4/3] flex items-center justify-center overflow-hidden rounded">
                        <img
                          src={certificate.src}
                          alt={certificate.alt}
                          className="max-w-full max-h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    </div>

                    {/* Certificate Info - Below the container */}
                    <div className="mt-4 text-center">
                      <h3
                        className="font-semibold text-base mb-2"
                        style={{
                          color: '#2D2D2D',
                          lineHeight: '1.4'
                        }}
                      >
                        {certificate.title}
                      </h3>
                      <p
                        className="text-sm mb-1"
                        style={{
                          color: '#555555'
                        }}
                      >
                        {certificate.institution}
                      </p>
                      {certificate.year && (
                        <p
                          className="text-sm"
                          style={{
                            color: '#777777'
                          }}
                        >
                          {certificate.year}
                        </p>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Navigation Arrows */}
            <button
              className="edu-prev-btn absolute left-0 top-1/3 -translate-y-1/2 text-[#1c1208] hover:text-[#3d2a1a] transition-colors z-10 w-12 h-12 rounded-full flex items-center justify-center cursor-pointer"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
              aria-label="Previous certificate"
            >
              <i className="ri-arrow-left-line text-2xl"></i>
            </button>

            <button
              className="edu-next-btn absolute right-0 top-1/3 -translate-y-1/2 text-[#1c1208] hover:text-[#3d2a1a] transition-colors z-10 w-12 h-12 rounded-full flex items-center justify-center cursor-pointer"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
              aria-label="Next certificate"
            >
              <i className="ri-arrow-right-line text-2xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-[#1c1208]/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Certificate lightbox"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors z-10 w-12 h-12 rounded-full flex items-center justify-center cursor-pointer"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
            aria-label="Close lightbox"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          {/* Navigation Arrows */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 w-12 h-12 rounded-full flex items-center justify-center cursor-pointer"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
            aria-label="Previous certificate"
          >
            <i className="ri-arrow-left-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 w-12 h-12 rounded-full flex items-center justify-center cursor-pointer"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
            aria-label="Next certificate"
          >
            <i className="ri-arrow-right-line text-2xl"></i>
          </button>

          {/* Certificate Image */}
          <div className="max-w-5xl max-h-full flex items-center justify-center">
            <img
              src={certificates[currentImageIndex].src}
              alt={certificates[currentImageIndex].alt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Certificate Info */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-center max-w-md">
            <h3
              className="text-lg font-semibold mb-2"
            >
              {certificates[currentImageIndex].title}
            </h3>
            <p
              className="text-sm opacity-80 mb-1"
            >
              {certificates[currentImageIndex].institution}
            </p>
            {certificates[currentImageIndex].year && (
              <p
                className="text-xs opacity-60"
              >
                {certificates[currentImageIndex].year}
              </p>
            )}
            <p
              className="text-xs opacity-50 mt-3"
            >
              {currentImageIndex + 1} of {certificates.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
}